import { SUPPORTED_LOCALES } from "@claroty/utilities/data/i18n/supportedLocales";
import { IStoreData } from "@claroty/utilities/store/IStoreData";
import getStore from "@claroty/utilities/store/store";
import ReduxStateType from "../reducers/ReduxStateType";
import * as initialState from "../reducers/initialState";
import { useCacheStore } from "./cache/useCacheStore";
import { useGlobalFiltersStore } from "./globalFilters/useGlobalFiltersStore";
import { useThemeStore } from "./theme/useThemeStore";

const getFromReduxStoreState = <T>(selector: (state: Partial<ReduxStateType>) => T): T => {
    // eslint-disable-next-line no-restricted-syntax
    const state = getStore()?.getState() ?? initialState;
    return selector(state) ?? selector(initialState);
};
/**
 * StoreData is an accessor layer for data in redux store that is accessed directly.
 *
 * Using StoreData allows you to use redux data without a React redux subscription such as `useSelector` or `mapStateToProps`
 * However, the data you access might get changed without triggering an update.
 *
 * - Use this class for fetching data that is used immediately for an event such as an API call or user action
 * - Do not use this class for data that will be used in the UI (unless you can ensure its freshness using subscribeStore
 *   or for Redux data that cannot change in the scope of the current page such as permissions).
 */
export const StoreDataFromRedux = {
    get dispatch() {
        // eslint-disable-next-line no-restricted-syntax
        return getStore()?.dispatch;
    },

    // User time settings
    get userTimezone() {
        return getFromReduxStoreState(state => state.user?.userDetails.user_preferences.timezone);
    },

    get userLocaleCode() {
        return getFromReduxStoreState(state => state.user?.userDetails.user_preferences.locale_code);
    },

    get userLanguageCode() {
        return (
            getFromReduxStoreState(state => state.user?.userDetails.user_preferences.language) ??
            SUPPORTED_LOCALES.en.id
        );
    },

    get userIs24HourClock() {
        return getFromReduxStoreState(state => state.user?.userDetails.user_preferences.is_24_hour_clock);
    },

    // Other user settings
    get userName() {
        return getFromReduxStoreState(state => state.user?.userDetails.username);
    },

    get userDisplayName() {
        return getFromReduxStoreState(state => state.user?.userDetails.display_name);
    },

    get userRoleNames() {
        return getFromReduxStoreState(state => state.user?.userDetails.role_names);
    },

    get userPreferences() {
        return getFromReduxStoreState(state => state.user?.userDetails.user_preferences);
    },

    get userEmail() {
        return getFromReduxStoreState(state => state.user?.userDetails.email);
    },

    // Org data
    get organizationNickname() {
        return getFromReduxStoreState(state => state.user?.organizationDetails.display_name);
    },

    get organizationTimezone() {
        return getFromReduxStoreState(
            state => state.user?.organizationDetails.timezone ?? state.user?.userDetails.user_preferences?.timezone,
        );
    },

    get organizationDefaultCountry() {
        return getFromReduxStoreState(state => state.user?.organizationDetails.default_country_code);
    },

    get isManagementOrg() {
        return getFromReduxStoreState(state => state.user?.organizationDetails.is_management_organization);
    },

    get siteDetails() {
        return getFromReduxStoreState(state => state.user?.siteDetails);
    },

    get loginAdminConfig() {
        return getFromReduxStoreState(state => state.admin.login);
    },

    // Routes and Permissions
    get isSuperUser() {
        return getFromReduxStoreState(state => state.user?.userDetails.is_superuser);
    },

    get permissions() {
        return getFromReduxStoreState(state => state.user?.userDetails.permissions);
    },

    get devicesConfiguration() {
        return getFromReduxStoreState(state => state.user?.configurations.devices_configuration);
    },

    get routes() {
        // { pageRoutes: preAuthRoutes |  MANAGEMENT_ORG_ROUTES | postAuthRoutes, redirectRoutes: ... }
        return getFromReduxStoreState(state => state.user?.routes ?? state.preAuth?.routes);
    },

    get globalFilters() {
        return useGlobalFiltersStore.getState().filters;
    },

    get darkMode() {
        return useThemeStore.getState().isDarkMode;
    },

    get roles() {
        return getFromReduxStoreState(state => state.admin.roles);
    },

    get defaultNotificationConfiguration() {
        return getFromReduxStoreState(state => state.admin.defaultNotificationsConfiguration);
    },

    get cache() {
        return useCacheStore.getState().cache;
    },
} satisfies IStoreData;
